/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-eq-null */

import { logger } from "@octopusdeploy/logging";
import type { ConfiguredFeatureToggles, FeaturesConfigurationResource, FeatureToggle, InstallationHistoryResource, UserPermissionSetResource, UserResource, GitHubAppSettings } from "@octopusdeploy/octopus-server-client";
import { UserPermissions } from "@octopusdeploy/octopus-server-client";
import { OpenFeature } from "@openfeature/web-sdk";
import { unstable_batchedUpdates } from "react-dom";
import { configurationActions } from "~/areas/configuration/reducers/configurationArea";
import { repository } from "~/clientInstance";
import { FeatureToggleProvider } from "~/featureToggleProvider";
import { OpenFeatureLoggerImpl } from "~/openFeatureLoggerImpl";
import store from "~/store";
import { OverridableOctopusFeatureToggleProvider } from "./overridableOctopusFeatureToggleProvider";

type OnChangeSubscription = () => void;

export class Session {
    initialized: boolean = false;
    currentUser: UserResource | null = undefined!;
    currentPermissions: UserPermissions | null = undefined!;
    featureToggles: ReadonlyArray<FeatureToggle> | null = null;
    featureToggleOverrides: Readonly<Record<string, boolean>> = {};
    featureToggleProvider: FeatureToggleProvider | undefined = undefined!;
    octopusFeatureToggleProvider: OverridableOctopusFeatureToggleProvider | undefined = undefined!;
    features: FeaturesConfigurationResource | null = undefined!;
    installationHistory: InstallationHistoryResource[] = [];
    gitHubAppSettings: GitHubAppSettings | null = null;

    private onChangeSubscriptions: OnChangeSubscription[] = [];

    async start(user: UserResource, featureConfigurations: FeaturesConfigurationResource, featureToggles: ConfiguredFeatureToggles, installationHistory: InstallationHistoryResource[], gitHubAppSettings: GitHubAppSettings) {
        // session start is currently called multiple times when loading the application
        // this is a temporary fix that #team-backend-foundations-requests will revert once the structural work is done
        // to ensure session start is more reliably executed once, and once only
        if (this.initialized) {
            return;
        }

        logger.info("Starting session for {user}.", { user });
        this.currentUser = user;
        this.featureToggles = featureToggles.FeatureToggles;
        this.features = featureConfigurations;
        this.installationHistory = installationHistory;
        this.gitHubAppSettings = gitHubAppSettings;
        await this.initializeOctopusFeatureToggles(user, featureToggles.FeatureToggles);
        store.dispatch(configurationActions.featuresFetched(featureConfigurations));
        this.initialized = true;
        this.onChange();
    }

    end() {
        // Batching state updates for the session end.
        // This prevents an issue where subscriptions or redux updates causes a re-render before all interested components have updated their state based on the new session state.
        // This can probably be removed in React 18 with the added automatic batching.
        unstable_batchedUpdates(() => {
            if (this.currentUser) {
                logger.info("Ending session for {user}.", { user: this.currentUser });
            }
            this.currentUser = null;
            this.currentPermissions = null;
            this.featureToggles = null;
            this.features = null;
            store.dispatch(configurationActions.featuresReset());
            this.initialized = false;
            this.onChange();
        });
    }

    async initializeOctopusFeatureToggles(user: UserResource, featureToggles: ReadonlyArray<FeatureToggle>) {
        try {
            const clientIdentifier = await repository.ServerConfiguration.featureToggleClientIdentifier();

            if (clientIdentifier) {
                const serverInformation = repository.getServerInformation();
                const provider = new OverridableOctopusFeatureToggleProvider({
                    clientIdentifier: clientIdentifier,
                    logger: new OpenFeatureLoggerImpl(logger),
                    releaseVersionOverride: serverInformation.version,
                });
                this.octopusFeatureToggleProvider = provider;
                await OpenFeature.setProviderAndWait(provider);
            } else {
                const provider = new FeatureToggleProvider(featureToggles, this.featureToggleOverrides);
                this.featureToggleProvider = provider;
                await OpenFeature.setProviderAndWait(provider);
            }

            await OpenFeature.setContext({
                username: user.Username,
                displayName: user.DisplayName,
                emailAddress: user.EmailAddress,
                serverUri: window.location.hostname,
            });
        } catch (e) {
            logger.error(e, "Failed to initialize Octopus Feature Toggles");
        }
    }

    refreshPermissions(userPermission: UserPermissionSetResource) {
        this.currentPermissions = UserPermissions.Create(userPermission.SpacePermissions, userPermission.SystemPermissions, userPermission.Teams);
        this.onChange();
    }

    isAuthenticated() {
        return this.currentUser != null;
    }

    subscribe(onChange: OnChangeSubscription) {
        this.onChangeSubscriptions.push(onChange);

        return () => {
            this.onChangeSubscriptions = this.onChangeSubscriptions.filter((s) => s !== onChange);
        };
    }

    onChange() {
        this.onChangeSubscriptions.forEach((s) => s());
    }

    setFeatureToggleOverrides(featureToggleOverrides: Record<string, boolean>) {
        if (!this.currentUser) {
            return;
        }

        this.featureToggleOverrides = featureToggleOverrides;

        if (this.featureToggleProvider) {
            this.featureToggleProvider.updateFeatures(this.featureToggles ?? [], featureToggleOverrides);
        }

        if (this.octopusFeatureToggleProvider) {
            this.octopusFeatureToggleProvider.updateOverrides(featureToggleOverrides);
        }
    }
}

export default Session;
